import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Select,
  MenuItem,
  Paper,
  useMediaQuery,
} from "@mui/material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PickUpMap from "../pages/PickUpMap";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePTagForm } from "../contexts/PTagFormContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getShopInfo } from "../services/api";
import { validatePhoneNumber, validateEmail } from "../utils/validation";
import { useTranslation } from 'react-i18next';
import { set } from "lodash";

export default function PTagPayment() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { form, setForm, addForm, totalForms, activeFormIndex, setActiveFormIndex, forms, incrementFormsAdded } = usePTagForm();
  const [shopInfo, setShopInfo] = useState(null);
  const [shop, setShop] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const isMobile = useMediaQuery("(max-width:900px)");
  const [showPopup, setShowPopup] = useState(false);
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit') || '';
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!edit) {
      addForm();
      setActiveFormIndex(totalForms);
    }
    getShopInfo().then((res) => {
      setShopInfo(res.data.shopInfo);
    });
  }, []);

  useEffect(() => {
    setFieldErrors((prev) => {
      const newErrors = { ...prev };

      // Phone number error translation
      if (
        prev.phoneNumber === "請輸入聯絡電話" ||
        prev.phoneNumber === "Please enter phone number"
      ) {
        newErrors.phoneNumber = t('EnterPhoneNumber');
      } else if (
        prev.phoneNumber === "聯絡電話只能包含數字" ||
        prev.phoneNumber === "Phone number can only contain numbers"
      ) {
        newErrors.phoneNumber = t('OnlyNumbers');
      } else if (
        prev.phoneNumber === "請輸入有效的電話號碼" ||
        prev.phoneNumber === "Please enter a valid phone number"
      ) {
        newErrors.phoneNumber = t('InvalidPhoneNumber');
      }

      // Email error translation
      if (
        prev.email === "請輸入電郵地址" ||
        prev.email === "Please enter email"
      ) {
        newErrors.email = t('EnterEmail');
      } else if (
        prev.email === "請輸入有效的電郵地址" ||
        prev.email === "Please enter a valid email"
      ) {
        newErrors.email = t('InvalidEmail');
      }

      // Last name error translation
      if (
        prev.lastName === "請輸入收件人姓名" ||
        prev.lastName === "Please enter recipient's name"
      ) {
        newErrors.lastName = t('EnterRecipientName');
      }

      // Pet name error translation
      if (
        prev.petName === "請輸入寵物名稱" ||
        prev.petName === "Please enter pet's name"
      ) {
        newErrors.petName = t('EnterPetName');
      }

      return newErrors;
    });
  }, [t]);

  const shipping = 50;

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));

    // Real-time validation for specific fields
    if (field === "phoneNumber") {
      const phoneError = validatePhoneNumber(value, t);
      setFieldErrors((prev) => ({
        ...prev,
        phoneNumber: phoneError,
      }));
    } else if (field === "email") {
      const emailError = validateEmail(value, t);
      setFieldErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    } else if (field === "petContact") {
      const phoneError = validatePhoneNumber(value, t);
      setFieldErrors((prev) => ({
        ...prev,
        petContact: phoneError,
      }));
    } else if (field === "lastName") {
      // Validate lastName name
      if (!value.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          lastName: t('EnterRecipientName'),
        }));
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          lastName: "",
        }));
      }
    } else if (field === "petName") {
      // Validate pet name
      if (!value.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          petName: t('EnterPetName'),
        }));
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          petName: "",
        }));
      }
    } else if (field === "delivery") {
      setForm(prev => ({
        ...prev,
        delivery: value,
        ...(value === "SF-HOME-DELIVERY"
          ? { netCode: "", serviceName: "", shippingAddress: "", attrName: "" }
          : {})
      }));
    } else {
      // Clear error when field is filled
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validation: all required fields must be filled and no validation errors
  const isCustom = form.version === "custom";
  const isFormValid =
    form.version &&
    form.size &&
    form.color &&
    (!isCustom || (form.petName && form.petContact)) &&
    !fieldErrors.petName &&
    !fieldErrors.petContact;

  const verifyCoupon = () => {
    form.coupon = form.coupon.toUpperCase();
    const shop = shopInfo.find((shop) => shop.shopCode === form.coupon);
    if (shop) {
      setShop(shop);
      setSuccess(t('validCode'));
      setError("");
    } else {
      setShop(null);
      setError(t('invalidCode'));
      setSuccess("");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setFileError(t('invalidFileType') || 'Please upload a PDF or image file (JPG, PNG, GIF)');
      return;
    }

    // Validate file size (2-10 MB)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      setFileError(t('fileTooLarge') || 'File size must not exceed 10 MB');
      return;
    }

    // File is valid
    setUploadedFile(file);
    setFileError("");
    setForm((prev) => ({ ...prev, discountProof: file }));
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
    setFileError("");
    setForm((prev) => ({ ...prev, discountProof: null }));
  };

  return (
    <Box
      sx={{ background: "#FCF8F3", minHeight: "100vh", pt: 10, position: "relative" }}
    >
      <Navbar />
      <Button
        sx={{ color: "black", minWidth: 0, position: "absolute", left: isMobile ? 10 : 80 }}
        onClick={() => edit ? navigate("/shopping_cart") : navigate("/ptag")}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="24" viewBox="0 0 14 24" fill="none">
          <path d="M12 22L2 12L12 2" stroke="#050505" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Button>
      <Box
        sx={{
          maxWidth: 1280,
          width: "100%",
          mx: "auto",
          px: { xs: 1, sm: 2 },
          pt: { xs: 2, md: 6 },
          pb: { xs: 4, md: 8 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: { xs: 4, md: 6 },
            width: "100%",
          }}
        >
          {/* Left: Order Summary */}
          <Box sx={{ flex: 1, minWidth: 0, mb: { xs: 4, md: 0 }, mt: 4, px: 1 }}>
            <Paper elevation={0} sx={{ background: "transparent", p: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <img
                  src="/icons/ptag-2.png"
                  alt="PTag"
                  style={{
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
                <Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: isMobile ? 6 : 28 }}>
                    <Box>
                      <Typography fontWeight={700}>
                        PTag
                      </Typography>
                      {form.version && (
                        <>
                          {form.size && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <g clipPath="url(#clip0_5995_46606)">
                                  <path d="M13.5801 6.84285V7.41075C13.5793 8.74187 13.1483 10.0371 12.3512 11.1032C11.5542 12.1694 10.4339 12.9493 9.15744 13.3267C7.88094 13.7042 6.51663 13.6588 5.268 13.1975C4.01936 12.7362 2.9533 11.8836 2.2288 10.767C1.50429 9.65027 1.16017 8.32931 1.24776 7.00107C1.33534 5.67282 1.84993 4.40848 2.71479 3.3966C3.57965 2.38471 4.74844 1.6795 6.04684 1.38615C7.34524 1.09279 8.70368 1.22701 9.91956 1.76877" stroke="#969696" strokeWidth="1.23457" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M13.5794 2.46875L7.40654 8.64776L5.55469 6.79591" stroke="#969696" strokeWidth="1.23457" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_5995_46606">
                                    <rect width="14.8148" height="14.8148" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                              <Typography fontSize={14} color="text.secondary">
                                {`${form.size} ${t('mm')}`}
                              </Typography>
                            </Box>)}
                          {form.color && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <g clipPath="url(#clip0_5995_46606)">
                                  <path d="M13.5801 6.84285V7.41075C13.5793 8.74187 13.1483 10.0371 12.3512 11.1032C11.5542 12.1694 10.4339 12.9493 9.15744 13.3267C7.88094 13.7042 6.51663 13.6588 5.268 13.1975C4.01936 12.7362 2.9533 11.8836 2.2288 10.767C1.50429 9.65027 1.16017 8.32931 1.24776 7.00107C1.33534 5.67282 1.84993 4.40848 2.71479 3.3966C3.57965 2.38471 4.74844 1.6795 6.04684 1.38615C7.34524 1.09279 8.70368 1.22701 9.91956 1.76877" stroke="#969696" strokeWidth="1.23457" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M13.5794 2.46875L7.40654 8.64776L5.55469 6.79591" stroke="#969696" strokeWidth="1.23457" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_5995_46606">
                                    <rect width="14.8148" height="14.8148" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                              <Typography fontSize={14} color="text.secondary">
                                {form.color === '金色' ? `${t('golden')}` : `${t('silver')}`}
                              </Typography>
                            </Box>)}
                        </>
                      )}
                    </Box>
                    <Box>
                      <Typography>
                        HKD ${form.version === "custom" ? 279 : 259}
                      </Typography>
                    </Box>
                  </Box>

                </Box>
              </Box>
              {form.version && (
                <>
                  {/* Coupon */}
                  <Box sx={{ mt: 4 }}>
                    <Typography fontWeight={700} fontSize={16} mb={1}>
                      {t('discountCode')}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder={t('discountCode')}
                        value={form.coupon}
                        onChange={handleChange("coupon")}
                        sx={{ flex: 1, background: "#fff" }}
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        sx={{
                          background: "#D9D9D9",
                          color: "#333",
                          boxShadow: "none",
                        }}
                        onClick={verifyCoupon}
                      >
                        {t('apply')}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <label htmlFor="discount-file-upload">
                        <input
                          id="discount-file-upload"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.gif"
                          style={{ display: "none" }}
                          onChange={handleFileUpload}
                        />
                        <Box sx={{
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.7 }
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M17 8L12 3L7 8" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 3V15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </Box>
                      </label>
                      <Typography fontSize={13}>
                        {t('discountInfo')}
                      </Typography>
                    </Box>

                    {/* File upload preview/status */}
                    {uploadedFile && (
                      <Box sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: '#E6FFF0',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M13 2V9H20" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <Box>
                            <Typography fontSize={12} fontWeight={600}>
                              {uploadedFile.name}
                            </Typography>
                            <Typography fontSize={10} color="text.secondary">
                              {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                          onClick={removeUploadedFile}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </Box>
                      </Box>
                    )}

                    {fileError && (
                      <Typography color="error" fontSize={12} mt={1} sx={{ backgroundColor: '#FFE6E6', p: 1, borderRadius: 1 }}>
                        {fileError}
                      </Typography>
                    )}
                  </Box>
                  {success && (
                    <Typography color="success" fontSize={13} mt={1} mb={1}>
                      {success}
                    </Typography>
                  )}
                  {error && (
                    <Typography color="error" fontSize={13} mt={1} mb={1}>
                      {error}
                    </Typography>
                  )}
                  {/* Price Summary */}
                  <Box sx={{ mt: 4 }}>
                    {form.shopName && (
                      <Typography
                        color="text."
                        fontSize={13}
                        my={1}
                        fontWeight={700}
                      >
                        {form.shopName}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography color="text.secondary">{t('subtotal')}</Typography>
                      <Typography>HKD ${shop ? shop.price : 259 + (form.version === "custom" ? 20 : 0)}</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography color="text.secondary">{t('shippingFee')}</Typography>
                      <Typography>HKD ${shipping}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography fontWeight={700}>{t('total')}</Typography>
                      <Typography
                        fontWeight={700}
                        fontSize={20}
                        color="#050505"
                      >
                        HKD ${(shop ? shop.price : 259 + (form.version === "custom" ? 20 : 0)) + shipping}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Paper>
          </Box>

          {/* Vertical Divider for desktop only */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" }, mx: 3, minHeight: 400 }}
          />

          {/* Horizontal divider for mobile only */}
          <Divider
            orientation="horizontal"
            flexItem
            sx={{ display: { xs: "block", md: "none" } }}
          />

          {/* Right: Form */}
          <Box
            sx={{ flex: 1, minWidth: 0, width: "100%", mt: { xs: 4, md: 0 }, px: 1, }}
          >
            <Paper elevation={0} sx={{ background: "transparent", p: 0 }}>
              {/* Product Options */}
              <Box mb={4}>
                <Typography fontWeight={700} fontSize={18} mb={2}>
                  {t('PtagStyle')}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, }}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">{t('Version')}</FormLabel>
                    <RadioGroup
                      value={form.version}
                      onChange={(e) => {
                        handleChange("version")(e);
                        if (form.coupon) {
                          verifyCoupon();
                        }
                      }}
                    >
                      <div style={{ display: "flex", gap: isMobile ? 0 : 20 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", width: "50%" }}>
                          <FormControlLabel
                            value="normal"
                            control={<Radio
                              sx={{
                                '&.Mui-checked': {
                                  color: '#FFB60C', // Checked color
                                },
                              }} />}
                            label={<Typography fontSize={isMobile ? '0.95rem' : '1rem'}>{`${t('normal')} HKD $259`}</Typography>}
                          />
                          <Typography color="text.secondary" fontSize={isMobile ? 10 : 14} ml={0.5}>
                            —
                          </Typography>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start", justifyContent: "center", width: "50%" }}>
                          <FormControlLabel
                            value="custom"
                            control={<Radio
                              sx={{
                                '&.Mui-checked': {
                                  color: '#FFB60C', // Checked color
                                },
                              }} />}
                            label={<Typography fontSize={isMobile ? '0.95rem' : '1rem'}>
                              {`${t('custom')} HKD $279`}
                            </Typography>}
                          />
                          <Typography color="text.secondary" fontSize={isMobile ? 10 : 14}>
                            <TaskAltIcon sx={{ fontSize: 16, mr: 1 }} />
                            {t('namePhotoPhoneNumber')}
                          </Typography>
                        </div>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">{t('dimensions')}</FormLabel>
                    <RadioGroup
                      row
                      value={form.size}
                      onChange={handleChange("size")}
                    >
                      <FormControlLabel
                        value="25"
                        control={<Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#FFB60C', // Checked color
                            },
                          }} />}
                        label={`25 ${t('mm')}`}
                      />
                      <FormControlLabel
                        value="30"
                        control={<Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#FFB60C', // Checked color
                            },
                          }} />}
                        label={`30 ${t('mm')}`}
                      />
                    </RadioGroup>
                  </FormControl>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">{t('color')}</FormLabel>
                    <RadioGroup
                      row
                      value={form.color}
                      onChange={handleChange("color")}
                    >
                      <FormControlLabel
                        value="金色"
                        control={<Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#FFB60C', // Checked color
                            },
                          }} />}
                        label={t('golden')}
                      />
                      <FormControlLabel
                        value="銀色"
                        control={<Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#FFB60C', // Checked color
                            },
                          }} />}
                        label={t('silver')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>

              {/* Conditionally show 打印內容 if version is custom */}
              {form.version === "custom" && (
                <Box mb={4}>
                  <Typography fontWeight={700} fontSize={18} mb={2}>
                    {t('PtagPrintContent')}
                  </Typography>
                  <Typography fontSize={18} mb={2}>
                    {t('petName')}
                  </Typography>
                  <TextField
                    placeholder={t('petName')}
                    fullWidth
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                    value={form.petName}
                    onChange={handleChange("petName")}
                    error={!!fieldErrors.petName}
                    helperText={fieldErrors.petName}
                  />

                  <Box sx={{ mb: 2 }}>
                    <Typography fontSize={18} mb={2}>
                      {t('photo')}
                    </Typography>
                    <label htmlFor="pet-photo-upload">
                      <Box
                        sx={{
                          width: "100%",
                          minHeight: 240,
                          background: "#fff",
                          border: "1px dashed #ccc",
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {form.photo ? (
                          <img
                            src={
                              typeof form.photo === "string"
                                ? form.photo
                                : URL.createObjectURL(form.photo)
                            }
                            alt="預覽"
                            style={{
                              width: "100%",
                              height: 300,
                              objectFit: "contain",
                              borderRadius: 8,
                              border: "30px solid white",
                              backgroundColor: "#fff"
                            }}
                          />
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                            <rect width="25" height="25" rx="12.5" fill="#FFF0CD" />
                            <path d="M12.5 8.33594V16.6693" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8.33203 12.5H16.6654" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        <input
                          id="pet-photo-upload"
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setForm((prev) => ({ ...prev, photo: file }));
                            }
                          }}
                        />
                      </Box>
                    </label>
                    <Typography color="text.secondary" fontSize={13} mt={1}>
                      {t('photoInfo')}
                    </Typography>
                  </Box>
                  <Typography fontSize={18} mb={2}>
                    {t('petContact')}
                  </Typography>
                  <TextField
                    placeholder="9123 4567"
                    fullWidth
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                    value={form.petContact}
                    onChange={handleChange("petContact")}
                    error={!!fieldErrors.petContact}
                    helperText={fieldErrors.petContact}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                  />
                </Box>
              )}

              <Button
                variant="contained"
                sx={{
                  background: "#FFB60C",
                  color: "#050505",
                  fontWeight: 700,
                  fontSize: 16,
                  borderRadius: 2,
                  px: 6,
                  py: 1.5,
                  mt: 2,
                  boxShadow: "none",
                  "&:hover": { background: "#FFB60C" },
                }}
                fullWidth
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    option: "PTag",
                    price: shop ? shop.price : 259 + (prev.version === "custom" ? 20 : 0),
                  }));
                  if (!edit) {
                    incrementFormsAdded();
                  }
                  navigate("/shopping_cart");
                }}
                disabled={!isFormValid}
              >
                {t('AddtoCart')}
              </Button>
            </Paper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}
